import {
    StepService,
    RegionElement,
    CommandWithFollowup,
    DigitalContentStepData,
    Step,
    LayoutsState,
    Region,
    ILayout,
    ImageElement,
    LayoutElementType,
} from "../../types";
import { toString as urlToString } from "qrcode";
import { shortenUrl } from "../shortener";
import { WorkflowManager } from "../../WorkflowManager";
import { Poller } from "../../util/Poller";
import {
    LayoutNotFoundError,
    MisconfigurationError,
    ResourceGenerationError,
    ResourceNotFoundError,
} from "../../util/exception";
import { getAssets } from "../asset";
import { CreateElementCommand, DeleteElementCommand, GroupCommand } from "../../command";
import { generate } from "../../util/guid";

export const MAX_UPLOAD_SIZE_IN_MB = 100;

class DigitalContentStepService implements StepService<DigitalContentStepData> {
    async init(
        stepData: Step<DigitalContentStepData>,
        workflowManager: WorkflowManager,
        reducerState?: LayoutsState,
    ): Promise<CommandWithFollowup | null> {
        if (reducerState) {
            await this.reload(stepData, workflowManager, reducerState);
        }
        return null;
    }

    private async reload(
        stepData: Step<DigitalContentStepData>,
        workflowManager: WorkflowManager,
        reducerState: LayoutsState,
    ) {
        const serializedStep = workflowManager.getSerializedStep(
            stepData.stepName,
            reducerState.serializableWorkflow.steps,
        );
        const layouts = Object.values(reducerState.layouts);
        const elements = layouts.map((layout) => layout.elements).flat();
        const elementsForStep = elements.filter((el) => el.stepName === stepData.stepName);
        const regionElements = elementsForStep.map((el) => ({
            id: el.id,
            region: el.stepRegion,
            regionIndex: el.stepRegionIndex,
        }));
        await workflowManager.setSelectionsAndElements(stepData.stepName, [], regionElements, async () => {
            const shortUrl = serializedStep?.storage?.videoShortUrl;
            const url = serializedStep?.storage?.videoUrl;
            workflowManager.updateStorage(stepData.stepName, {
                videoShortUrl: shortUrl,
                videoUrl: url,
            });
        });
    }

    async regenerateQRCode(
        elements: RegionElement[],
        assetKey: string,
        workflowManager: WorkflowManager,
        stepData: Step<DigitalContentStepData>,
    ) {
        const getMpegLink = async () => {
            const asset = (await getAssets([assetKey]))[0];
            const link = asset?.versions?.find((v) => v.name === "mpeg4")?.link;
            return { asset, link };
        };

        // Asset versions are attached to the asset shortly after asset upload. We poll to find these versions and
        // grab the link for the mp4 variant.
        const videoLink = await new Promise<{ rel: string; href: string }>((resolve, reject) => {
            new Poller(
                async () => !!(await getMpegLink()).link,
                async () => {
                    const linkData = await getMpegLink();
                    if (!linkData.link || !linkData.link) {
                        throw new ResourceNotFoundError(linkData.asset);
                    }
                    resolve({
                        rel: "mpeg4",
                        href: linkData.link,
                    });
                },
                () => {
                    reject("Poller timed out with 40 attempts @ 3 second interval");
                },
                3000,
                40,
            );
        });

        elements.forEach((elem) => workflowManager.getCommandDispatcher()(new DeleteElementCommand(elem.id)));
        const protocolPrefix = stepData.data.baseUrl.slice(0, 4) === "http" ? "" : "https://";
        const urlBuilder = new URL(protocolPrefix + stepData.data.baseUrl);
        urlBuilder.searchParams.append("video", btoa(JSON.stringify([videoLink])));

        //iOS does not recognise a URL without the trailing slash on the pathname,
        urlBuilder.pathname = urlBuilder.pathname + (urlBuilder.pathname.slice(-1) === "/" ? "" : "/");

        const builtUrl = urlBuilder.toString();
        if (builtUrl.length >= 2000) {
            throw new ResourceGenerationError("Cannot create QR code, URL too long.");
        }
        const shortUrl = await shortenUrl(builtUrl);

        if (!stepData.data || !stepData.data.regions) {
            throw new MisconfigurationError(stepData, "Missing regions.");
        }
        const regionElements = await this.regionElements(stepData);
        const result = await this.command(shortUrl, regionElements, workflowManager, stepData.stepName);
        if (result) {
            result.command && workflowManager.getCommandDispatcher()(result.command);
            result.followup && (await result.followup());
        }
        await workflowManager.setSelectionsAndElements(stepData.stepName, [], regionElements, async () => {
            workflowManager.updateStorage(stepData.stepName, {
                videoShortUrl: shortUrl,
                videoUrl: videoLink.href,
            });
        });

        return shortUrl;
    }

    private async regionElements(stepData: Step<DigitalContentStepData>): Promise<RegionElement[]> {
        const createElementForRegion = (region: Region, regionIndex: number): RegionElement => {
            const elementKey = generate();
            return { id: elementKey, region, regionIndex };
        };

        return stepData.data.regions.map(createElementForRegion);
    }

    private async command(
        shortUrl: string,
        regionElements: RegionElement[],
        workflowManager: WorkflowManager,
        stepName: string,
    ): Promise<CommandWithFollowup | null> {
        const layouts = workflowManager.getLayouts();
        const base64data = btoa(await urlToString(shortUrl, { type: "svg" }));
        const dataUri = `data:image/svg+xml;base64,${base64data}`;
        const commands = regionElements.map((regionElement) => {
            const region = regionElement.region;
            const layout = layouts.find((layout: ILayout) => layout.panelId === region?.panelId);

            if (!layout && region) {
                throw new LayoutNotFoundError(region);
            }

            if (layout && !region) {
                throw new Error("Region not found");
            }

            if (!layout || !region) {
                throw new Error("Neither a region or layout found!");
            }

            return new CreateElementCommand<ImageElement>(
                {
                    stepRegion: region,
                    stepRegionIndex: regionElement.regionIndex,
                    stepName: stepName,
                    id: regionElement.id,
                    src: dataUri,
                    type: LayoutElementType.Image,
                    y: region.top,
                    x: region.left,
                    width: region.width,
                    height: region.height,
                    rotation: 0,
                },
                layout,
            );
        });
        return { command: new GroupCommand(commands), followup: async () => {} };
    }
}

export const digitalContentStepService = new DigitalContentStepService();
